import ObjectProcessor from "@/lib/ObjectProcessor";
import environment from "../environment";
import { getAccountInfo, logout } from "../cloud/user";
import { fetchCurrentUser } from "@/api/parse";

const { isServer } = environment;

export class UserSession {
  private static name = "parseUser";
  private static _user: User | null = null;
  private static events: ((user: typeof this._user) => void)[] = [];

  static get = () => {
    if (isServer) return null;
    if (!this._user) {
      const user = window.localStorage.getItem(this.name);
      if (user) this._user = JSON.parse(user);
    }
    return this._user;
  };

  static set = (value: typeof this._user) => {
    this._user = value;

    if (value && !isServer) {
      window.localStorage.setItem(this.name, JSON.stringify(value));
      document.cookie = `parseUser64=${ObjectProcessor.encodeObjURI(value)}; path = /`;
    } else if (!value && !isServer) {
      localStorage.removeItem(this.name);
      document.cookie = "parseUser64=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
    }

    this.emit();
  };

  static get isThirdPartyAccount() {
    if (!this._user) return false;
    return Boolean(this._user.authData.google?.id || this._user.authData.apple?.id);
  }

  static subscribe = (callback: (user: typeof this._user) => void) => {
    this.events.push(callback);
    return () => {
      this.events = this.events.filter((cb) => cb !== callback);
    };
  };

  private static emit = () => {
    this.events.forEach((cb) => cb(this._user));
  };

  static syncAccountInfo = async () => {
    const accountInfo = await getAccountInfo();
    if (this._user) this._user.accountInfo = accountInfo;
    this.emit();
    return accountInfo;
  };

  static syncSession = async (sessionToken?: string) => {
    const _sessionToken = sessionToken || this._user?.sessionToken;
    if (!_sessionToken) return;
    const user = await fetchCurrentUser(_sessionToken);
    user.accountInfo = await getAccountInfo();
    this.set(user);
  };

  static getHasPremium = async (fetch = true) => {
    const accountInfo = !fetch && this._user?.accountInfo ? this._user.accountInfo : await this.syncAccountInfo();
    return Boolean(
      accountInfo.subscription && new Date(accountInfo.subscription.expirationDate.iso).getTime() > Date.now()
    );
  };

  static getHasPremiumFromSubscription = (subscription?: UserAccountInfo["subscription"]) => {
    if (!subscription) return false;
    return new Date(subscription.expirationDate.iso).getTime() > Date.now();
  };

  static destroy = async () => {
    await logout();
    this.set(null);
  };
}
